import React, { useEffect, useState } from "react";
import {
  BarChart3, Eye, Heart, Ticket, CalendarDays, Calendar, Clock, MapPin,
  ChevronRight, Plus, ArrowUpRight, ArrowDownRight, Trophy,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import Lottie from "lottie-react";
import nodata from "../assets/nodata.json";
import { getDashboard } from "../services/CreateurService";

// ══════════════════════════════════════════════════════════
//  CONSTANTES & HELPERS
// ══════════════════════════════════════════════════════════
const SERIES = [
  { cle: "views", label: "Vues", couleur: "#2563EB" },
  { cle: "likes", label: "Likes", couleur: "#DB2777" },
  { cle: "clicks", label: "Clics", couleur: "#7C3AED" },
];

const MOIS = ["JAN", "FÉV", "MAR", "AVR", "MAI", "JUIN", "JUIL", "AOÛ", "SEP", "OCT", "NOV", "DÉC"];
const JOURS = ["Dim.", "Lun.", "Mar.", "Mer.", "Jeu.", "Ven.", "Sam."];

const compacter = (n = 0) => {
  n = Number(n) || 0;
  if (n >= 1e6) return (n / 1e6).toFixed(1).replace(".0", "") + "M";
  if (n >= 1e3) return (n / 1e3).toFixed(1).replace(".0", "") + "k";
  return String(n);
};

const infosDate = (iso) => {
  if (!iso) return null;
  const d = new Date(String(iso).replace(" ", "T"));
  if (isNaN(d)) return null;
  return {
    jour: String(d.getDate()).padStart(2, "0"),
    mois: MOIS[d.getMonth()],
    long: `${JOURS[d.getDay()]} ${d.getDate()} ${MOIS[d.getMonth()].toLowerCase()}.`,
    heure: `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`,
    court: `${d.getDate()}/${d.getMonth() + 1}`,
  };
};

const lieu = (e) => [e.rue, e.ville].filter(Boolean).join(", ");

// ══════════════════════════════════════════════════════════
//  PETITS BLOCS
// ══════════════════════════════════════════════════════════
const Bloc = ({ titre, icone: Icone, action, children }) => (
  <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
    <div className="flex items-center justify-between mb-4">
      <h2 className="flex items-center gap-2 text-sm font-bold text-gray-900">
        <Icone size={16} className="text-violet-600" /> {titre}
      </h2>
      {action}
    </div>
    {children}
  </section>
);

const LienVoirTout = ({ onClick }) => (
  <button onClick={onClick} className="flex items-center text-xs font-semibold text-violet-600 hover:text-violet-800">
    Voir tout <ChevronRight size={14} />
  </button>
);

// ── Bannière : prochains événements (défile toutes les 5 s) ──
const Banniere = ({ events, onVoir, onCreer }) => {
  const [i, setI] = useState(0);

  useEffect(() => {
    if (events.length <= 1) return;
    const t = setInterval(() => setI((x) => (x + 1) % events.length), 5000);
    return () => clearInterval(t);
  }, [events.length]);

  const e = events[i];
  if (!e) return null;
  const d = infosDate(e.date);

  return (
    <div className="relative h-52 sm:h-60 rounded-2xl overflow-hidden bg-gray-900">
      {e.img && <img key={e.id} src={e.img} alt={e.title} className="absolute inset-0 w-full h-full object-cover" />}
      <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/50 to-transparent" />

      {/* Bouton créer — coin haut droit */}
      <button onClick={onCreer}
        className="absolute top-4 right-4 z-10 flex items-center gap-1.5 bg-violet-600 hover:bg-violet-700 text-white text-xs font-bold px-3.5 py-2 rounded-lg shadow transition">
        <Plus size={14} /> <span className="hidden sm:inline">Créer un événement</span>
      </button>

      <div className="relative h-full flex flex-col justify-end p-5 sm:p-7 text-white max-w-lg">
        <span className="self-start mb-2 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-violet-600">
          {e.category || "Prochain événement"}
        </span>
        <h2 className="text-xl sm:text-3xl font-extrabold leading-tight line-clamp-2">{e.title}</h2>
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-gray-200">
          {d && <span className="flex items-center gap-1"><Calendar size={13} /> {d.long}</span>}
          {d && <span className="flex items-center gap-1"><Clock size={13} /> {d.heure}</span>}
          {lieu(e) && <span className="flex items-center gap-1 truncate"><MapPin size={13} /> {lieu(e)}</span>}
        </div>
        <button onClick={() => onVoir(e.id)}
          className="self-start mt-4 flex items-center gap-1 bg-white text-gray-900 hover:bg-violet-50 px-4 py-2 rounded-lg text-xs font-bold transition">
          Voir l'événement <ChevronRight size={14} />
        </button>
      </div>

      {events.length > 1 && (
        <div className="absolute bottom-5 right-6 flex gap-1.5">
          {events.map((_, k) => (
            <button key={k} onClick={() => setI(k)}
              className={`h-1.5 rounded-full transition-all ${k === i ? "w-6 bg-white" : "w-1.5 bg-white/50"}`} />
          ))}
        </div>
      )}
    </div>
  );
};

// ── KPI ───────────────────────────────────────────────────
const Kpi = ({ label, valeur, pourcent, icone: Icone, teinte, loading }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
    <div className="flex items-center justify-between">
      <span className="text-xs font-medium text-gray-500">{label}</span>
      <span className={`p-1.5 rounded-lg ${teinte}`}><Icone size={15} /></span>
    </div>
    {loading ? (
      <div className="h-8 w-20 mt-2 rounded bg-gray-100 animate-pulse" />
    ) : (
      <div className="flex items-end gap-2 mt-2">
        <span className="text-2xl font-extrabold text-gray-900 leading-none">{compacter(valeur)}</span>
        {!!pourcent && Number(valeur) > 0 && (
          <span className={`flex items-center text-[11px] font-bold ${pourcent > 0 ? "text-emerald-600" : "text-red-500"}`}>
            {pourcent > 0 ? <ArrowUpRight size={13} /> : <ArrowDownRight size={13} />}
            {Math.abs(pourcent)}%
          </span>
        )}
      </div>
    )}
  </div>
);

// ── Prochainement ─────────────────────────────────────────
const Prochainement = ({ events, onVoir }) => {
  if (events.length === 0) {
    return <p className="text-sm text-gray-400 text-center py-8">Aucun événement à venir.</p>;
  }
  return (
    <div className="flex flex-col gap-1">
      {events.map((e) => {
        const d = infosDate(e.date);
        return (
          <button key={e.id} onClick={() => onVoir(e.id)}
            className="flex items-center gap-3 p-2 rounded-xl hover:bg-gray-50 text-left transition">
            {d && (
              <div className="w-12 h-12 shrink-0 rounded-xl bg-violet-50 flex flex-col items-center justify-center">
                <span className="text-base font-extrabold text-violet-700 leading-none">{d.jour}</span>
                <span className="text-[9px] font-bold text-violet-500">{d.mois}</span>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">{e.title}</p>
              <p className="text-[11px] text-gray-500 truncate">
                {d?.heure}{lieu(e) ? ` · ${lieu(e)}` : ""}
              </p>
            </div>
            <span className="flex items-center gap-1 text-[11px] font-semibold text-gray-600 shrink-0">
              <Ticket size={12} className="text-violet-500" /> {e.reserved ?? 0}
            </span>
          </button>
        );
      })}
    </div>
  );
};

// ── Top événements ────────────────────────────────────────
const TopEvents = ({ events, onVoir }) => {
  const [cle, setCle] = useState("views");
  const serie = SERIES.find((s) => s.cle === cle);
  const liste = [...events].sort((a, b) => (b[cle] || 0) - (a[cle] || 0)).slice(0, 5);
  const max = Math.max(1, ...liste.map((e) => e[cle] || 0));

  return (
    <>
      <div className="flex gap-1 bg-gray-100 p-0.5 rounded-lg mb-4 w-fit">
        {SERIES.map((s) => (
          <button key={s.cle} onClick={() => setCle(s.cle)}
            className={`px-3 py-1 rounded-md text-[11px] font-semibold transition
              ${cle === s.cle ? "bg-white shadow-sm text-gray-900" : "text-gray-500"}`}>
            {s.label}
          </button>
        ))}
      </div>

      {liste.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-8">Aucun événement.</p>
      ) : (
        <div className="flex flex-col gap-3">
          {liste.map((e, k) => (
            <button key={e.id} onClick={() => onVoir(e.id)} className="flex items-center gap-3 text-left group">
              <span className="w-4 text-xs font-bold text-gray-400">{k + 1}</span>
              <div className="w-9 h-9 shrink-0 rounded-lg overflow-hidden bg-gray-100">
                {e.img && <img src={e.img} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-gray-800 truncate group-hover:text-violet-700">{e.title}</p>
                <div className="h-1.5 mt-1.5 rounded-full bg-gray-100 overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-700"
                    style={{ width: `${Math.max(4, ((e[cle] || 0) / max) * 100)}%`, backgroundColor: serie.couleur }} />
                </div>
              </div>
              <span className="text-xs font-bold text-gray-900 w-10 text-right">{compacter(e[cle])}</span>
            </button>
          ))}
        </div>
      )}
    </>
  );
};

// ── Courbe 30 jours ───────────────────────────────────────
const Courbe = ({ donnees }) => {
  const [actives, setActives] = useState({ views: true, likes: true, clicks: true });
  if (!donnees.length) return <p className="text-sm text-gray-400 text-center py-10">Pas encore de données.</p>;

  const L = 600, H = 160;
  const visibles = SERIES.filter((s) => actives[s.cle]);
  const max = Math.max(1, ...donnees.flatMap((d) => visibles.map((s) => d[s.cle])));
  const x = (i) => (donnees.length === 1 ? L / 2 : (i / (donnees.length - 1)) * L);
  const y = (v) => H - (v / max) * H;
  const ligne = (c) => donnees.map((d, i) => `${i ? "L" : "M"} ${x(i)} ${y(d[c])}`).join(" ");

  return (
    <>
      <div className="flex flex-wrap gap-2 mb-3">
        {SERIES.map((s) => (
          <button key={s.cle} onClick={() => setActives((a) => ({ ...a, [s.cle]: !a[s.cle] }))}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs border transition
              ${actives[s.cle] ? "border-gray-200 text-gray-700" : "border-gray-100 text-gray-300"}`}>
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: actives[s.cle] ? s.couleur : "#D1D5DB" }} />
            {s.label} <b>{compacter(donnees.reduce((a, d) => a + d[s.cle], 0))}</b>
          </button>
        ))}
      </div>
      <svg viewBox={`0 -8 ${L} ${H + 16}`} className="w-full h-44" preserveAspectRatio="none">
        {[0, 0.5, 1].map((p) => <line key={p} x1="0" x2={L} y1={H * p} y2={H * p} stroke="#F3F4F6" />)}
        {visibles.map((s) => (
          <path key={s.cle} d={ligne(s.cle)} fill="none" stroke={s.couleur} strokeWidth="2.5"
            strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
        ))}
      </svg>
      <div className="flex justify-between text-[10px] text-gray-400 mt-1">
        <span>{infosDate(donnees[0].date)?.court}</span>
        <span>{infosDate(donnees[donnees.length - 1].date)?.court}</span>
      </div>
    </>
  );
};

// ══════════════════════════════════════════════════════════
//  PAGE
// ══════════════════════════════════════════════════════════
const Dashboard = () => {
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    (async () => {
      setLoading(true);
      try {
        setData(await getDashboard(token));
      } catch (err) {
        console.error("Erreur dashboard :", err);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const voir = (id) => navigate(`/EspaceClient/mesevents/view/${id}`);
  const creer = () => navigate("/EspaceClient/mesevents/ajouter");

  const s = data?.statistics || {};
  const top = data?.topEvents?.length ? data.topEvents : data?.lastEvents || [];
  const aVenir = data?.upcomingEvents || [];
  const banniere = (aVenir.length ? aVenir : top).slice(0, 3);
  const vide = !loading && top.length === 0 && aVenir.length === 0;

  return (
    <div className="w-full flex justify-center pb-20 font-Poppins bg-white">
      <div className="w-full max-w-6xl space-y-5">

        {vide ? (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm flex flex-col items-center py-14 gap-3">
            <Lottie animationData={nodata} loop className="w-32 h-32" />
            <p className="text-sm text-gray-500">Vous n'avez encore aucun événement.</p>
            <button onClick={creer}
              className="flex items-center gap-1.5 bg-violet-600 text-white text-xs font-bold px-4 py-2 rounded-lg">
              <Plus size={14} /> Créer mon premier événement
            </button>
          </div>
        ) : (
          <>
            {/* Bannière */}
            {loading
              ? <div className="h-56 rounded-2xl bg-gray-100 animate-pulse" />
              : <Banniere events={banniere} onVoir={voir} onCreer={creer} />}

            {/* KPI */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              <Kpi label="Événements" valeur={s.totalEvents} pourcent={s.totalEventsPercent}
                icone={CalendarDays} teinte="bg-emerald-50 text-emerald-600" loading={loading} />
              <Kpi label="Vues" valeur={s.totalViews} pourcent={s.totalViewsPercent}
                icone={Eye} teinte="bg-blue-50 text-blue-600" loading={loading} />
              <Kpi label="Likes" valeur={s.totalLikes} pourcent={s.totalLikesPercent}
                icone={Heart} teinte="bg-pink-50 text-pink-600" loading={loading} />
              <Kpi label="Réservations" valeur={s.totalReserved}
                icone={Ticket} teinte="bg-violet-50 text-violet-600" loading={loading} />
            </div>

            {/* Prochainement + Top */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5">
              <Bloc titre="Prochainement" icone={Calendar}
                action={<LienVoirTout onClick={() => navigate("/EspaceClient/mesevents")} />}>
                {loading ? <div className="h-48 rounded-xl bg-gray-50 animate-pulse" />
                  : <Prochainement events={aVenir} onVoir={voir} />}
              </Bloc>

              <Bloc titre="Top événements" icone={Trophy}
                action={<LienVoirTout onClick={() => navigate("/EspaceClient/mesevents")} />}>
                {loading ? <div className="h-48 rounded-xl bg-gray-50 animate-pulse" />
                  : <TopEvents events={top} onVoir={voir} />}
              </Bloc>
            </div>

            {/* Courbe */}
            <Bloc titre="Évolution sur 30 jours" icone={BarChart3}>
              {loading ? <div className="h-44 rounded-xl bg-gray-50 animate-pulse" />
                : <Courbe donnees={data?.evolution || []} />}
            </Bloc>
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;